import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import * as Permissions from "../../supportFunctions/Permissions";
import {
  PageHeader,
  useTreeData,
  confirmDialog,
} from "@velocity-ehs/component-library";
import { useTranslation } from "react-i18next";
import { useFlags } from "launchdarkly-react-client-sdk";
import PersonForm, { Mode, PersonCustomerRelationship } from "./PersonForm";
import { Person, PersonAttr, OptionType, mapGenderToOption } from "./Person";
import { UserDetails } from "./UserDetails";
import UsersService from "@velocity-ehs/user-utils-module";
import VerticalTabs, { TabItem } from "../../sharedComponents/VerticalTabs";
import { useForm } from "react-hook-form";
import { toJSDate } from "./HelperFunctions";
import Roles from "./Roles";
import {
  getStatusIndicatorProps,
  statusEnum,
} from "../../sharedComponents/StatusIndicator";
import { useGetPerson } from "../../../hooks/useGetPerson";
import usePersonMutation, {
  PersonActions,
} from "../../../hooks/usePersonMutation";
import { MakePersonUserSlider } from "./MakePersonUserSlider";

export const mapCurrentPersonToForm = (
  person: PersonAttr,
  treeData?: any
): Person => {
  if (!person) return;
  const relationshipToCustomer = PersonCustomerRelationship().find(
    (el) => el.value === person.relationshipToCustomer
  );

  const primaryLocation = treeData?.active.flatTree.find(
    (el) => el.id === person.primaryLocation
  );

  const gender = mapGenderToOption(
    (person?.personSensitive?.gender as string) ?? ""
  );

  return {
    customerId: UsersService.customer.id,
    username: person?.user?.displayedLoginName,
    relationshipToCustomer,
    primaryLocation,
    active: person?.active,
    address2: person?.personSensitive?.address2,
    birthDate: toJSDate(person?.personSensitive?.birthDate),
    city: person?.personSensitive?.city,
    contractingCompany: person.contractingCompany,
    country: person?.personSensitive?.country,
    currentPosition: person?.personSensitive?.currentPosition,
    currentPositionStartDate: toJSDate(
      person?.personSensitive?.currentPositionStartDate
    ),
    displayName: person?.displayName,
    effectiveDate: toJSDate(person?.effectiveDate),
    email: person?.email,
    employeeID: person?.employeeID,
    endDate: toJSDate(person?.endDate),
    faxNumber: person?.faxNumber,
    firstName: person?.firstName,
    gender: gender as OptionType,
    hireDate: toJSDate(person?.personSensitive?.hireDate),
    id: person.id,
    lastName: person?.lastName,
    middleName: person?.middleName,
    phone: person?.phone,
    stateProv: person?.personSensitive?.stateProv,
    streetAddress: person?.personSensitive?.streetAddress,
    supervisor: person?.supervisor,
    userId: person?.user?.id,
    yearsInJob: person?.yearsInJob,
    zipPostal: person?.personSensitive?.zipPostal,
  };
};

const EditPerson: React.FC<{ editPermission: string[] }> = ({
  editPermission = [],
}) => {
  const { personUserChangeTracking } = useFlags();

  const [showSlider, setShowSlider] = useState(false);
  const [targetOverride, setTargetOverride] = useState<string[]>([]);

  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const { person, isFetching, refetch } = useGetPerson(id);

  const history = useHistory();
  const { treeStore, isAllFetched } = useTreeData();

  const formMethods = useForm<Person>({
    mode: "onBlur",
  });

  const personDetailMode = Permissions.IsAllowed(
    editPermission,
    UsersService.permissions
  )
    ? Mode.Edit
    : Mode.View;

  useEffect(() => {
    person && formMethods.reset(mapCurrentPersonToForm(person, treeStore));
    // eslint-disable-next-line
  }, [person, isAllFetched]);

  const { mutations } = usePersonMutation();

  const updatePersonForm = (formValues: Person) => {
    return mutations.mutate(PersonActions.UPDATE, formValues, id).then(() => {
      refetch();
    });
  };

  const formatPersonUser = () => {
    return {
      id: person?.user?.id,
      displayedLoginName: person?.user?.displayedLoginName,
      active: person?.user?.active,
      userDemographic: { email: person?.user?.userDemographic?.email },
      customerId: person?.user?.customer?.id,
      customer: {
        customerDomainName: person?.user?.customer?.customerDomainName,
      },
    };
  };

  const activateInactivateUser = () => {
    const input = {
      active: !person.active,
      userId: person.userId,
      username: person.user.displayedLoginName,
      customerId: UsersService.customer.id,
    };

    return mutations
      .mutate(PersonActions.ACTIVATE_INACTIVATE_USER, input)
      .then(() => {
        refetch();
      });
  };

  const getTabs = (): TabItem[] => {
    const tabs: TabItem[] = [
      {
        title: t("profile_Personal_Details_Tab"),
        content: (
          <>
            <PersonForm
              mode={personDetailMode}
              isLoading={isFetching}
              formMethods={formMethods}
              onSubmit={updatePersonForm}
            />
          </>
        ),
      },
    ];

    if (person?.userId) {
      tabs.push({
        content: (
          <UserDetails
            personUser={formatPersonUser()}
            personId={person?.id}
            readOnly={!UsersService.permissions.canEditUsers}
            refetch={refetch}
            isLoading={isFetching}
          />
        ),
        title: t("person_user_details"),
        path: "/userDetails",
      });
    }

    if (person?.userId) {
      tabs.push({
        content: <Roles person={{ ...person }} />,
        title: t("universal_Roles"),
        path: "/roles",
      });
    }

    return tabs;
  };

  const renderTertiaryButton = () => {
    if (!person?.userId) {
      return {
        label: t("make_person_user_button_text"),
        id: "makePersonUserButton",
        type: "button",
        onClick: () => setShowSlider(true),
      };
    }

    return {
      label: person.active
        ? t("universal_Inactivate")
        : t("universal_Activate"),
      id: "activateInactivateButton",
      type: "button",
      onClick: () =>
        confirmDialog({
          message: person.active
            ? t("message_user_inactive_log_in")
            : t("message_user_active_log_in"),
          header: t("universal_Confirm"),
          accept: () => activateInactivateUser(),
          reject: () => {},
          acceptLabel: person.active
            ? t("universal_Inactivate")
            : t("universal_Activate"),
          rejectLabel: t("universal_Cancel"),
        }),
    };
  };

  useEffect(() => {
    if (person) {
      setTargetOverride([person.id, person.userId]);
    }
  }, [person]);

  return (
    <>
      <PageHeader
        title={person ? person?.displayName : undefined}
        navigationTree={[
          { text: `Accelerate ${t("universal_Home")}`, href: "/" },
          { text: t("universal_persons_users"), href: "/persons" },
          { text: t("action_edit"), href: "" },
        ]}
        tertiaryButton={renderTertiaryButton()}
        secondaryButton={{
          label: t("universal_Cancel"),
          id: "cancel",
          type: "button",
          onClick: () => history.push("/persons"),
        }}
        primaryButton={{
          label: t("universal_Save"),
          type: "submit",
          id: "personForm",
          form: "personForm",
          disabled: !Permissions.IsAllowed(
            editPermission,
            UsersService.permissions
          ),
          loading: mutations.isLoading,
        }}
        statusIndicator={
          person?.userId
            ? getStatusIndicatorProps({
                status: person.active ? statusEnum.Active : statusEnum.Inactive,
              })
            : undefined
        }
      />
      <VerticalTabs
        tabs={getTabs()}
        targetOverride={targetOverride}
        includeChangeTracking={personUserChangeTracking}
        targetIdentifierType="PERSON"
      />
      <MakePersonUserSlider
        closeSliderCallback={() => setShowSlider(false)}
        showSlider={showSlider}
        person={{
          id: person?.id,
          email: person?.email,
          firstName: person?.firstName,
          lastName: person?.lastName,
        }}
        refetchCallback={refetch}
      />
    </>
  );
};

export default EditPerson;
